import { useState } from 'react';
import { connect } from 'react-redux';

/* Styled Components */
import {
  CallToActionTitle,
  CallToActionSubtitle,
  FormSection,
  LegalLink,
  LegalText,
  PageFooter,
  ClickableText,
} from 'styles/signup/MultiScreenSignupPage';

/* Child Components */
import Button, { BUTTON_COLOR_KEYS } from 'components/common/Button';
import WishTextInput from 'components/common/WishTextInput';

/* Actions */
import {
  submitSignupStep as submitSignupStepAction,
  setFirstName as setFirstNameAction,
  setLastName as setLastNameAction,
  setEmail as setEmailAction,
  setPassword as setPasswordAction,
  setErrorMessage as setErrorMessageAction,
  clearErrorMessage as clearErrorAction,
} from 'app/signupSlice';

/* Selectors */
import {
  getFirstName,
  getLastName,
  getEmail,
  getPassword,
  getErrorMessage,
} from 'app/signupSlice';

/* Utils */
import { i18n } from 'utils/i18n';

/* Constants */
import {
  MIN_PASSWORD_LENGTH,
  MAX_PASSWORD_LENGTH,
  SIGNUP_SCREEN,
} from 'constants/SignupConstants';
import { RETAILER_TERMS_ROUTE } from 'constants/RouteConstants';

/* Types */
import type { AppState, AppDispatch } from 'app/store';
import type { OptionsType as SubmitSignupStepOptionsType } from 'services/SubmitSignupStepService';

const mapStateToProps = (state: AppState) => ({
  firstName: getFirstName(state),
  lastName: getLastName(state),
  email: getEmail(state),
  password: getPassword(state),
  errorMessage: getErrorMessage(state),
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  submitSignupStep: (args: {
    signupStep: Values<typeof SIGNUP_SCREEN>;
    nextSignupStep: Values<typeof SIGNUP_SCREEN>;
    options: SubmitSignupStepOptionsType;
  }) => dispatch(submitSignupStepAction(args)),
  clearError: () => dispatch(clearErrorAction()),
  setErrorMessage: (errorMessage: string) =>
    dispatch(setErrorMessageAction(errorMessage)),
  setFirstName: (firstName: string) => dispatch(setFirstNameAction(firstName)),
  setLastName: (lastName: string) => dispatch(setLastNameAction(lastName)),
  setEmail: (email: string) => dispatch(setEmailAction(email)),
  setPassword: (password: string) => dispatch(setPasswordAction(password)),
});

type PropsType = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const CreateAccountPage = ({
  firstName,
  lastName,
  email,
  password,
  errorMessage,
  clearError,
  setErrorMessage,
  setFirstName,
  setLastName,
  setEmail,
  setPassword,
  submitSignupStep,
}: PropsType) => {
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  const togglePasswordVisible = () => setPasswordVisible(!passwordVisible);

  const onInputChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    clearError();
    const { value } = event.currentTarget;
    const { id } = event.currentTarget.dataset;
    switch (id) {
      case 'firstName':
        setFirstName(value);
        break;
      case 'lastName':
        setLastName(value);
        break;
      case 'email':
        setEmail(value);
        break;
      case 'password':
        setPassword(value);
        break;
      default:
        break;
    }
  };

  const trimmedFirstName = firstName.trim();
  const trimmedLastName = lastName.trim();
  const trimmedEmail = email.trim();
  const isValidPassword =
    password != null &&
    Boolean(password) &&
    password.length >= MIN_PASSWORD_LENGTH &&
    password.length <= MAX_PASSWORD_LENGTH;
  const isFormComplete =
    [firstName, lastName, email, password].every(
      val => val && Boolean(val.trim()),
    ) && isValidPassword;

  const showError = Boolean(errorMessage);

  const onSubmit = async () => {
    if (!isFormComplete) {
      setErrorMessage(i18n('Complete all required fields'));
      return;
    }
    submitSignupStep({
      signupStep: SIGNUP_SCREEN.CREATE_ACCOUNT,
      nextSignupStep: SIGNUP_SCREEN.STORE_TYPE,
      options: {
        firstName,
        lastName,
        email,
        password,
      },
    });
  };

  return (
    <>
      <CallToActionTitle>{i18n('Let’s get started!')}</CallToActionTitle>
      <CallToActionSubtitle>
        {i18n('Join for free. No monthly fees.')}
      </CallToActionSubtitle>

      <FormSection>
        <FormSection flex>
          <WishTextInput
            value={firstName}
            title={i18n('First name')}
            stretch
            margin="16px 8px 0 0"
            data-id="firstName"
            onChange={onInputChange}
            errorMessage={i18n('Required')}
            isErrorShown={!trimmedFirstName && showError}
          />
          <WishTextInput
            value={lastName}
            title={i18n('Last name')}
            stretch
            margin="16px 0 0 0"
            data-id="lastName"
            onChange={onInputChange}
            errorMessage={i18n('Required')}
            isErrorShown={!trimmedLastName && showError}
          />
        </FormSection>
        <WishTextInput
          value={email}
          title={i18n('Email')}
          stretch
          margin="16px 0 0 0"
          data-id="email"
          onChange={onInputChange}
          errorMessage={i18n('Required')}
          isErrorShown={!trimmedEmail && showError}
        />
        <WishTextInput
          value={password}
          title={i18n('Password')}
          stretch
          margin="16px 0 0 0"
          data-id="password"
          onChange={onInputChange}
          type={passwordVisible ? 'text' : 'password'}
          rightSideElement={
            <ClickableText onClick={togglePasswordVisible}>
              {passwordVisible ? i18n('HIDE') : i18n('SHOW')}
            </ClickableText>
          }
          errorMessage={
            (!password && i18n('Required')) || !isValidPassword
              ? i18n(
                  'Password length must be between {%1=min password length} and {%2=max password length}',
                  MIN_PASSWORD_LENGTH,
                  MAX_PASSWORD_LENGTH,
                )
              : undefined
          }
          isErrorShown={!isValidPassword && showError}
        />
      </FormSection>
      <PageFooter justifyStart>
        <Button
          onClick={onSubmit}
          color={BUTTON_COLOR_KEYS.DARK_COBALT}
          stretch
          bold
          overrideStyle={{
            margin: '24px 0',
          }}
        >
          {i18n('Create account')}
        </Button>
        <LegalText centerTextAlign>
          {i18n(
            'By tapping "Create account", you agree to the %1$s.',
            <LegalLink
              target="_blank"
              href={RETAILER_TERMS_ROUTE}
              rel="noopener noreferrer"
            >
              {i18n('Retailer Terms of Service')}
            </LegalLink>,
          )}
        </LegalText>
      </PageFooter>
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateAccountPage);
